import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';

export default function CookieBanner() {
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  
  // Preferences state
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);

  useEffect(() => {
    // Check if consent has already been given
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      setIsVisible(true);
    } else {
      // Apply existing consent to PostHog
      if (window.posthog) {
        if (consent === 'accepted' || (consent === 'custom' && localStorage.getItem('cookie_analytics') === 'true')) {
          window.posthog.opt_in_capturing();
        } else {
          window.posthog.opt_out_capturing();
        }
      }
    }

    // Listen for custom event to open preferences from footer
    const handleOpenPreferences = () => {
      setShowPreferences(true);
      // Pre-fill current settings if they exist
      const consent = localStorage.getItem('cookie_consent');
      if (consent === 'accepted') {
        setAnalyticsEnabled(true);
      } else if (consent === 'custom') {
        setAnalyticsEnabled(localStorage.getItem('cookie_analytics') === 'true');
      } else {
        setAnalyticsEnabled(false);
      }
    };
    
    window.addEventListener('open-cookie-preferences', handleOpenPreferences);
    return () => window.removeEventListener('open-cookie-preferences', handleOpenPreferences);
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem('cookie_consent', 'accepted');
    localStorage.setItem('cookie_analytics', 'true');
    if (window.posthog) window.posthog.opt_in_capturing();
    setIsVisible(false);
    setShowPreferences(false);
  };

  const handleRejectAll = () => {
    localStorage.setItem('cookie_consent', 'rejected');
    localStorage.setItem('cookie_analytics', 'false');
    if (window.posthog) window.posthog.opt_out_capturing();
    setIsVisible(false);
    setShowPreferences(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookie_consent', 'custom');
    localStorage.setItem('cookie_analytics', analyticsEnabled ? 'true' : 'false');
    
    if (window.posthog) {
      if (analyticsEnabled) {
        window.posthog.opt_in_capturing();
      } else {
        window.posthog.opt_out_capturing();
      }
    }
    
    setIsVisible(false);
    setShowPreferences(false);
  };

  if (!isVisible && !showPreferences) return null;

  return createPortal(
    <>
      {/* Banner */}
      {isVisible && !showPreferences && (
        <div className="fixed bottom-0 left-0 right-0 z-50 p-4 sm:p-6 sm:pb-8 flex justify-center pointer-events-none">
          <div className="bg-white rounded-xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-[#e5e9ee] p-5 sm:p-6 max-w-[900px] w-full pointer-events-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div className="flex-1">
              <h3 className="font-['Fraunces',serif] text-xl font-bold text-[#021426] mb-2">Your privacy matters</h3>
              <p className="text-sm text-[#4C545B] leading-relaxed">
                We use necessary technologies to keep AI Search Advisers secure and working. With your permission, we may also use optional technologies to understand performance and improve the experience.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto shrink-0">
              <button 
                onClick={() => setShowPreferences(true)}
                className="px-4 py-2.5 text-sm font-semibold text-[#4C545B] bg-transparent border border-[#e5e9ee] rounded hover:bg-[#FAF7F2] transition-colors whitespace-nowrap"
              >
                Manage preferences
              </button>
              <button 
                onClick={handleRejectAll}
                className="px-4 py-2.5 text-sm font-semibold text-[#021426] bg-transparent border border-[#021426] rounded hover:bg-[#FAF7F2] transition-colors whitespace-nowrap"
              >
                Reject optional
              </button>
              <button 
                onClick={handleAcceptAll}
                className="px-4 py-2.5 text-sm font-semibold text-[#021426] bg-[#FEAB3D] rounded hover:opacity-90 transition-opacity whitespace-nowrap shadow-[0_4px_14px_rgba(254,171,61,0.3)]"
              >
                Accept optional
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preferences Modal */}
      {showPreferences && (
        <div className="fixed inset-0 z-[10030] flex items-center justify-center p-4 bg-[#021426]/60 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-[600px] max-h-[90vh] flex flex-col overflow-hidden">
            <div className="p-6 border-b border-[#e5e9ee] flex justify-between items-center bg-[#FAF7F2]">
              <h3 className="font-['Fraunces',serif] text-2xl font-bold text-[#021426]">Cookie preferences</h3>
              <button 
                onClick={() => isVisible ? setShowPreferences(false) : setShowPreferences(false)}
                className="text-[#4C545B] hover:text-[#021426] p-2"
                aria-label="Close"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1">
              <p className="text-[#4C545B] mb-6 text-sm leading-relaxed">
                Choose which optional technologies AI Search Advisers may use. Necessary technologies remain enabled because they are required for core functionality and security.
              </p>

              {/* Strictly Necessary */}
              <div className="border border-[#e5e9ee] rounded-lg p-5 mb-4 bg-white">
                <div className="flex justify-between items-start mb-2">
                  <h4 className="font-semibold text-[#021426] text-lg">Strictly Necessary</h4>
                  <span className="text-xs font-semibold uppercase tracking-wider text-[#0C5B94] bg-[#0C5B94]/10 px-2 py-1 rounded">Always active</span>
                </div>
                <p className="text-sm text-[#4C545B] leading-relaxed">
                  Required for authentication, security, session management, and remembering your privacy choices. The website cannot function properly without these.
                </p>
              </div>

              {/* Analytics */}
              <div className="border border-[#e5e9ee] rounded-lg p-5 bg-white">
                <div className="flex justify-between items-start mb-2">
                  <h4 className="font-semibold text-[#021426] text-lg">Analytics & Performance</h4>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      className="sr-only peer" 
                      checked={analyticsEnabled}
                      onChange={(e) => setAnalyticsEnabled(e.target.checked)}
                    />
                    <div className="w-11 h-6 bg-[#e5e9ee] peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#0C5B94]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-[#e5e9ee] after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#0C5B94]"></div>
                  </label>
                </div>
                <p className="text-sm text-[#4C545B] leading-relaxed">
                  We use PostHog analytics to understand how visitors navigate the website, identify technical errors, and improve performance.
                </p>
              </div>
            </div>
            
            <div className="p-6 border-t border-[#e5e9ee] flex flex-col sm:flex-row justify-end gap-3 bg-white">
              <button 
                onClick={handleSavePreferences}
                className="px-5 py-2.5 text-sm font-semibold text-[#021426] bg-transparent border border-[#021426] rounded hover:bg-[#FAF7F2] transition-colors w-full sm:w-auto"
              >
                Save preferences
              </button>
              <button 
                onClick={handleRejectAll}
                className="px-5 py-2.5 text-sm font-semibold text-[#021426] bg-transparent border border-[#021426] rounded hover:bg-[#FAF7F2] transition-colors w-full sm:w-auto"
              >
                Reject optional
              </button>
              <button 
                onClick={handleAcceptAll}
                className="px-5 py-2.5 text-sm font-semibold text-[#021426] bg-[#FEAB3D] rounded hover:opacity-90 transition-opacity shadow-[0_4px_14px_rgba(254,171,61,0.3)] w-full sm:w-auto"
              >
                Accept all
              </button>
            </div>
          </div>
        </div>
      )}
    </>, document.body
  );
}
